'use client';

import { c as _c } from "react/compiler-runtime";
import './header.scss';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { usePathname } from 'next/navigation';
import { Suspense, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { LogoName } from '@sola/schema/lib/logos.ts';
import type { Link as LinkProps } from '@sola/schema/lib/schema.ts';
import type { HeaderProps } from '@sola/schema/lib/skippy.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { HeaderDesktopActions, HeaderMobileActions } from './header-actions.tsx';
import { DesktopMenuItem } from './header-desktop-item.tsx';
import { MobileMenu } from './header-mobile-menu.tsx';
import { useHeaderLogic } from './header.logic.ts';

/**
 * Watches for pathname changes and calls onNavigate.
 * Wrapped in Suspense so usePathname() doesn't block the static shell.
 */
const PathnameWatcher = t0 => {
  const $ = _c(5);
  const {
    onNavigate
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== onNavigate) {
    t1 = () => {
      onNavigate();
    };
    $[0] = onNavigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== pathname) {
    t2 = [pathname, onNavigate];
    $[2] = onNavigate;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  return null;
};
type CustomizeProps = {
  logo?: LogoName;
  link?: LinkProps;
  showShoppingBag?: boolean;
  showLogin?: boolean;
  showSearch?: boolean;
  urlLogin?: string;
  urlAccountHome?: string;
  userMenu?: React.ReactNode;
  /**
   * Set by apps where middleware guarantees an authenticated user (my), so the
   * account area doesn't flash a login button while auth resolves.
   */
  assumeAuthenticated?: boolean;
};
export const Header: React.FC<HeaderProps & CustomizeProps> = t0 => {
  const $ = _c(121);
  const {
    primaryMenu,
    logo: t1,
    link,
    showShoppingBag,
    showLogin,
    showSearch,
    urlLogin,
    urlAccountHome,
    userMenu,
    assumeAuthenticated
  } = t0;
  const logo = t1 === undefined ? "compassion" : t1;
  let t2;
  if ($[0] !== primaryMenu.items) {
    t2 = primaryMenu.items || [];
    $[0] = primaryMenu.items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const menuItems = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["priority"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    t
  } = useTranslation(t3);
  let t4;
  if ($[3] !== menuItems) {
    t4 = {
      menuItems
    };
    $[3] = menuItems;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    menuOpen,
    navKey,
    navStack,
    setNavStack,
    isFullscreenMenuOpen,
    headerHidden,
    refs: t5,
    hoverHandlers: t6,
    callbacks: t7
  } = useHeaderLogic(t4);
  const {
    mobileCloseButtonRef,
    mobileOpenButtonRef,
    accountTriggerRef,
    hamburgerTriggerRef
  } = t5;
  const {
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
    setActiveItem
  } = t6;
  const {
    handlePathnameChange,
    handleMobileClose,
    handleMobileBack,
    handleAccountOpenFromHamburger,
    handleAccountClose,
    handleActionNavValueChange
  } = t7;
  let t8;
  if ($[5] !== headerHidden) {
    t8 = cn("Header", {
      "Header--hidden": headerHidden
    });
    $[5] = headerHidden;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== handlePathnameChange) {
    t9 = <Suspense><PathnameWatcher onNavigate={handlePathnameChange} /></Suspense>;
    $[7] = handlePathnameChange;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t) {
    t10 = t("header.skipLink");
    $[9] = t;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10) {
    t11 = <ButtonStyle><a className="Header__skip-link" href="#main">{t10}</a></ButtonStyle>;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== isFullscreenMenuOpen) {
    t12 = cn("Header__container", {
      "is-mobile-open": isFullscreenMenuOpen
    });
    $[13] = isFullscreenMenuOpen;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = link?.link || "/";
  let t14;
  if ($[15] !== link?.title || $[16] !== t) {
    t14 = link?.title || t("header.logoAlt");
    $[15] = link?.title;
    $[16] = t;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== logo) {
    t15 = <LogoChooser name={logo} className="Header__logo" color="full" aria-labelledby="Header-logo-label" priority={true} />;
    $[18] = logo;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const t16 = link?.title;
  let t17;
  if ($[20] !== t || $[21] !== t16) {
    t17 = t("header.logoAlt", {
      title: t16
    });
    $[20] = t;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t17) {
    t18 = <span id="Header-logo-label" className="sr-only">{t17}</span>;
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t13 || $[26] !== t14 || $[27] !== t15 || $[28] !== t18) {
    t19 = <div className="Header__logo-container"><Link href={t13} title={t14}>{t15}{t18}</Link></div>;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = `main-nav-${navKey}`;
  let t21;
  if ($[30] !== handleContentEnter || $[31] !== handleContentLeave || $[32] !== handleTriggerEnter || $[33] !== handleTriggerLeave || $[34] !== menuItems || $[35] !== menuOpen || $[36] !== setActiveItem) {
    let t22;
    if ($[38] !== handleContentEnter || $[39] !== handleContentLeave || $[40] !== handleTriggerEnter || $[41] !== handleTriggerLeave || $[42] !== menuOpen || $[43] !== setActiveItem) {
      t22 = item => <DesktopMenuItem key={item.title} item={item} menuOpen={menuOpen} handleTriggerEnter={handleTriggerEnter} handleTriggerLeave={handleTriggerLeave} handleContentEnter={handleContentEnter} handleContentLeave={handleContentLeave} setActiveItem={setActiveItem} />;
      $[38] = handleContentEnter;
      $[39] = handleContentLeave;
      $[40] = handleTriggerEnter;
      $[41] = handleTriggerLeave;
      $[42] = menuOpen;
      $[43] = setActiveItem;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    t21 = menuItems.map(t22);
    $[30] = handleContentEnter;
    $[31] = handleContentLeave;
    $[32] = handleTriggerEnter;
    $[33] = handleTriggerLeave;
    $[34] = menuItems;
    $[35] = menuOpen;
    $[36] = setActiveItem;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[45] !== t21) {
    t22 = <NavigationMenu.List className="Header__menu-list" asChild={true}><div><ul className="Header__desktop-items xl:mx-roomy-xs">{t21}</ul></div></NavigationMenu.List>;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <NavigationMenu.NavigationMenuIndicator />;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== handleViewportEnter || $[49] !== handleViewportLeave) {
    t24 = <div className="Header__viewport-position"><NavigationMenu.Viewport className="Header__menu-viewport flex justify-center" onPointerEnter={handleViewportEnter} onPointerLeave={handleViewportLeave} onFocus={handleViewportEnter} onBlur={handleViewportLeave} /></div>;
    $[48] = handleViewportEnter;
    $[49] = handleViewportLeave;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== menuOpen || $[52] !== t20 || $[53] !== t22 || $[54] !== t24) {
    t25 = <NavigationMenu.Root key={t20} aria-label="Main navigation" value={menuOpen} className="Header__root flex-col hidden lg:flex">{t22}{t23}{t24}</NavigationMenu.Root>;
    $[51] = menuOpen;
    $[52] = t20;
    $[53] = t22;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = `action-nav-${navKey}`;
  let t27;
  if ($[56] !== accountTriggerRef || $[57] !== assumeAuthenticated || $[58] !== handleAccountClose || $[59] !== showLogin || $[60] !== showSearch || $[61] !== showShoppingBag || $[62] !== urlAccountHome || $[63] !== urlLogin || $[64] !== userMenu) {
    t27 = <HeaderDesktopActions showSearch={showSearch} showShoppingBag={showShoppingBag} showLogin={showLogin} urlLogin={urlLogin} urlAccountHome={urlAccountHome} userMenu={userMenu} assumeAuthenticated={assumeAuthenticated} accountTriggerRef={accountTriggerRef} onAccountClose={handleAccountClose} />;
    $[56] = accountTriggerRef;
    $[57] = assumeAuthenticated;
    $[58] = handleAccountClose;
    $[59] = showLogin;
    $[60] = showSearch;
    $[61] = showShoppingBag;
    $[62] = urlAccountHome;
    $[63] = urlLogin;
    $[64] = userMenu;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t) {
    t28 = () => {
      trackClick(t("header.menu"));
    };
    $[66] = t;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t) {
    t29 = t("header.menu");
    $[68] = t;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ArkIcon iconName="menu-01" className="Header__nav-icon" />;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== hamburgerTriggerRef || $[72] !== t28 || $[73] !== t29) {
    t31 = <NavigationMenu.Trigger ref={hamburgerTriggerRef} onPointerEnter={_temp} onPointerMove={_temp2} onPointerLeave={_temp3} onClick={t28} className="flex items-center p-comfy-xs rounded-full" title={t29}>{t30}</NavigationMenu.Trigger>;
    $[71] = hamburgerTriggerRef;
    $[72] = t28;
    $[73] = t29;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== assumeAuthenticated || $[76] !== handleAccountOpenFromHamburger || $[77] !== showLogin || $[78] !== showSearch || $[79] !== showShoppingBag || $[80] !== urlAccountHome || $[81] !== urlLogin || $[82] !== userMenu) {
    t32 = <HeaderMobileActions showSearch={showSearch} showShoppingBag={showShoppingBag} showLogin={showLogin} urlLogin={urlLogin} urlAccountHome={urlAccountHome} userMenu={userMenu} assumeAuthenticated={assumeAuthenticated} onAccountOpen={handleAccountOpenFromHamburger} />;
    $[75] = assumeAuthenticated;
    $[76] = handleAccountOpenFromHamburger;
    $[77] = showLogin;
    $[78] = showSearch;
    $[79] = showShoppingBag;
    $[80] = urlAccountHome;
    $[81] = urlLogin;
    $[82] = userMenu;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== handleMobileBack || $[85] !== handleMobileClose || $[86] !== mobileCloseButtonRef || $[87] !== mobileOpenButtonRef || $[88] !== navStack || $[89] !== setNavStack || $[90] !== showLogin || $[91] !== t32) {
    t33 = <MobileMenu navStack={navStack} setNavStack={setNavStack} onClose={handleMobileClose} onBack={handleMobileBack} mobileCloseButtonRef={mobileCloseButtonRef} mobileOpenButtonRef={mobileOpenButtonRef} showLogin={showLogin}>{t32}</MobileMenu>;
    $[84] = handleMobileBack;
    $[85] = handleMobileClose;
    $[86] = mobileCloseButtonRef;
    $[87] = mobileOpenButtonRef;
    $[88] = navStack;
    $[89] = setNavStack;
    $[90] = showLogin;
    $[91] = t32;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  let t34;
  if ($[93] !== t31 || $[94] !== t33) {
    t34 = <NavigationMenu.Item className="Header__nav-action Header__mobile-menu-trigger flex lg:hidden items-center" key="mobile hamburger menu" value="mobile" asChild={true}><div>{t31}{t33}</div></NavigationMenu.Item>;
    $[93] = t31;
    $[94] = t33;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] !== t27 || $[97] !== t34) {
    t35 = <NavigationMenu.List className="Header__menu-list" asChild={true}><div className="Header__desktop-actions flex items-center gap-comfy-sm justify-end"><div className="flex flex-row gap-snug-md items-center md:gap-snug-xl lg:gap-comfy-sm">{t27}{t34}</div></div></NavigationMenu.List>;
    $[96] = t27;
    $[97] = t34;
    $[98] = t35;
  } else {
    t35 = $[98];
  }
  let t36;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <NavigationMenu.NavigationMenuIndicator />;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  let t37;
  if ($[100] !== handleViewportEnter || $[101] !== handleViewportLeave) {
    t37 = <div className="Header__viewport-position"><NavigationMenu.Viewport className="Header__menu-viewport flex justify-center" onPointerEnter={handleViewportEnter} onPointerLeave={handleViewportLeave} /></div>;
    $[100] = handleViewportEnter;
    $[101] = handleViewportLeave;
    $[102] = t37;
  } else {
    t37 = $[102];
  }
  let t38;
  if ($[103] !== handleActionNavValueChange || $[104] !== menuOpen || $[105] !== t26 || $[106] !== t35 || $[107] !== t37) {
    t38 = <NavigationMenu.Root key={t26} onValueChange={handleActionNavValueChange} value={menuOpen} className="Header__root flex flex-col" aria-label="Action navigation">{t35}{t36}{t37}</NavigationMenu.Root>;
    $[103] = handleActionNavValueChange;
    $[104] = menuOpen;
    $[105] = t26;
    $[106] = t35;
    $[107] = t37;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  let t39;
  if ($[109] !== t19 || $[110] !== t25 || $[111] !== t38) {
    t39 = <div className="Header__menu-list-container w-full">{t19}{t25}{t38}</div>;
    $[109] = t19;
    $[110] = t25;
    $[111] = t38;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== t12 || $[114] !== t39) {
    t40 = <div className={t12}>{t39}</div>;
    $[113] = t12;
    $[114] = t39;
    $[115] = t40;
  } else {
    t40 = $[115];
  }
  let t41;
  if ($[116] !== t11 || $[117] !== t40 || $[118] !== t8 || $[119] !== t9) {
    t41 = <header role="banner" className={t8}>{t9}{t11}{t40}</header>;
    $[116] = t11;
    $[117] = t40;
    $[118] = t8;
    $[119] = t9;
    $[120] = t41;
  } else {
    t41 = $[120];
  }
  return t41;
};
export default Header;
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp3(e_1) {
  return e_1.preventDefault();
}